import type { MapFeatureValue } from "../api/contracts";
import type { CompositeFeatureResult, CompositePreset, CompositeWeightMap, MetricDirection, MetricId } from "../types";

export const METRIC_DIRECTIONS: Record<MetricId, MetricDirection> = {
  "median-annual-pay": "higher-better",
  "mean-annual-pay": "higher-better",
  "weekly-pay": "higher-better",
  "employment-rate": "higher-better",
  "gdhi-per-head": "higher-better",
  "gva-per-head": "higher-better",
  "life-expectancy": "higher-better",
  "nvq4-qualifications-rate": "higher-better",
  "ev-charger-density": "higher-better",
  population: "higher-better",

  "housing-affordability-ratio": "lower-better",
  "co2-emissions-per-capita": "lower-better",
  "claimant-count-rate": "lower-better",
  "average-house-price": "lower-better",
  "gdp-per-head": "higher-better",
  "business-demography": "higher-better",
  "modelled-unemployment": "lower-better",
  "fuel-poverty": "lower-better",
  "children-in-relative-poverty": "lower-better",
  "net-additions-to-the-housing-stock": "higher-better",
  "gigabit-capable-broadband": "higher-better",
  "network-coverage-5g": "higher-better",
  "adult-obesity-prevalence": "lower-better",
  "personal-wellbeing": "higher-better",
  "first-time-buyer-mortgage-sales": "higher-better",
  "gcse-attainment": "higher-better",
  "pupils-at-expected-standard": "higher-better",
  "apprenticeship-starts": "higher-better",
  "fe-learner-achievements": "higher-better",
  "fe-participation": "higher-better",
  "early-years-language": "higher-better",
  "coverage-4g": "higher-better",
  "car-commute-time": "lower-better",
  "no-qualifications": "lower-better",
  "persistent-absences-all": "lower-better",
  "persistent-absences-fsm": "lower-better",
  "child-obesity": "lower-better",
  "adult-smokers": "lower-better",
  "cardiovascular-mortality": "lower-better",
  "electricity-consumption": "lower-better",
  "gas-consumption": "lower-better",
  "premises-below-30mbps": "lower-better",
  "population-density": "higher-better",
};

export const COMPOSITE_PRESETS: CompositePreset[] = [
  {
    id: "affluence-savings",
    name: "Affluence & Wealth",
    description: "High annual earnings and strong disposable income",
    weights: {
      "median-annual-pay": 50,
      "gdhi-per-head": 50,
    },
  },
  {
    id: "affordability-housing",
    name: "Housing Costs",
    description: "Lowest ratio of house prices to pay and manageable housing costs",
    weights: {
      "housing-affordability-ratio": 60,
      "average-house-price": 40,
    },
  },
  {
    id: "green-wellbeing",
    name: "Green & Healthy",
    description: "High life expectancy, low carbon emissions per person, and dense EV charger infrastructure",
    weights: {
      "life-expectancy": 40,
      "co2-emissions-per-capita": 40,
      "ev-charger-density": 20,
    },
  },
  {
    id: "opportunity-skills",
    name: "Jobs & Skills",
    description: "High employment rate, degree-level qualifications, and high Gross Value Added (GVA)",
    weights: {
      "employment-rate": 40,
      "nvq4-qualifications-rate": 40,
      "gva-per-head": 20,
    },
  },
];

export function isMetricId(value: string): value is MetricId {
  return value in METRIC_DIRECTIONS;
}

export function computeCompositeScores(
  slices: Map<MetricId, MapFeatureValue[]>,
  weights: CompositeWeightMap,
): Map<string, CompositeFeatureResult> {
  const activeEntries = Object.entries(weights).filter((entry): entry is [MetricId, number] => {
    const [id, weight] = entry;
    return isMetricId(id) && typeof weight === "number" && weight > 0;
  });

  const totalWeight = activeEntries.reduce((sum, [, w]) => sum + w, 0);
  const results = new Map<string, CompositeFeatureResult>();

  if (activeEntries.length === 0 || totalWeight <= 0) {
    return results;
  }

  // Precompute min and max for each active metric
  const metricBounds = new Map<MetricId, { min: number; max: number }>();
  for (const [metricId] of activeEntries) {
    const values = slices.get(metricId) ?? [];
    const nums = values.map((v) => v.value).filter((val): val is number => val !== null && !Number.isNaN(val));

    if (nums.length > 0) {
      metricBounds.set(metricId, {
        min: Math.min(...nums),
        max: Math.max(...nums),
      });
    }
  }

  // Collect all unique geography codes
  const geoCodes = new Set<string>();
  for (const [metricId] of activeEntries) {
    const values = slices.get(metricId) ?? [];
    for (const val of values) {
      geoCodes.add(val.geographyCode);
    }
  }

  // Compute composite score for each geography
  for (const code of geoCodes) {
    let weightedSum = 0;
    let validWeightSum = 0;

    for (const [metricId, weight] of activeEntries) {
      const bounds = metricBounds.get(metricId);
      if (!bounds || bounds.max === bounds.min) continue;

      const sliceValues = slices.get(metricId) ?? [];
      const obs = sliceValues.find((v) => v.geographyCode === code);
      if (!obs || obs.value === null || Number.isNaN(obs.value)) continue;

      let norm = (obs.value - bounds.min) / (bounds.max - bounds.min);
      const direction = METRIC_DIRECTIONS[metricId] || "higher-better";

      if (direction === "lower-better") {
        norm = 1 - norm;
      }

      weightedSum += norm * weight;
      validWeightSum += weight;
    }

    if (validWeightSum > 0) {
      const score = Math.round((weightedSum / validWeightSum) * 1000) / 10; // 0..100 with 1 decimal
      results.set(code, {
        geographyCode: code,
        compositeScore: Math.min(100, Math.max(0, score)),
      });
    }
  }

  return results;
}
